import { SimpleTreeManager } from './simple-tree-manager';
import { TreeManager } from './tree-core/tree-manager';
import { TreeView, enumerateTree } from './tree-core/tree-view';

type Change =
  | {
    readonly type: 'move';
    readonly key: string;
    readonly parent: string | undefined;
    readonly index: number | undefined;
  }
  | {
    readonly type: 'remove';
    readonly key: string;
  };

const cloneTree = <T>(origTree: TreeView<T>) => {
  const newTree = new SimpleTreeManager<T>();

  for (const node of enumerateTree(origTree)) {
    newTree.addNode(node.key, node.data, node.parent, node.index);
  }

  return newTree;
};

export class MutableTreeManager<T> implements TreeManager<T> {
  private changes: Change[] = [];
  private tree = cloneTree(this.origTree);

  constructor(private readonly origTree: SimpleTreeManager<T>) {}

  //#region TreeManager

  get roots(): readonly string[] {
    return this.tree.roots;
  }

  tryGetNode(key: string) {
    return this.tree.tryGetNode(key);
  }

  getNode(key: string) {
    return this.tree.getNode(key);
  }

  //#endregion

  //#region TreeManager

  addNode(key: string, data: T, parent: string | undefined, index: number) {
    this.origTree.addNode(key, data, parent, index);
    this.tree.addNode(key, data, parent, index);
    // FIXME: what if we already added a child to this parent with a moveNode?
    // we should track per-parent changes and replay the center-drops
  }

  updateNode(key: string, data: T) {
    this.origTree.updateNode(key, data);
    this.tree.updateNode(key, data);
  }

  moveNode(key: string, parent: string | undefined, index: number | undefined): void {
    this.tree.moveNode(key, parent, index);

    this.changes.push({
      type: 'move',
      key,
      parent,
      index,
    });
  }

  removeNode(key: string): void {
    this.tree.removeNode(key);

    this.changes.push({ type: 'remove', key });
  }

  //#endregion

  reset() {
    const changes = this.changes;
    this.changes = [];
    this.tree = cloneTree(this.origTree);
    return changes;
  }

  replay(changes: Change[]) {
    for (const change of changes) {
      if (change.type === 'remove') {
        this.removeNode(change.key);
      } else {
        this.moveNode(change.key, change.parent, change.index);
      }
    }
  }
}
